import { Box, IconButton, Tooltip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DashboardIcon from "@material-ui/icons/Dashboard";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import LayersIcon from "@material-ui/icons/Layers";
import PeopleIcon from "@material-ui/icons/People";
import PersonIcon from "@material-ui/icons/Person";
import PowerIcon from "@material-ui/icons/Power";
import RoomIcon from "@material-ui/icons/Room";
import SecurityIcon from "@material-ui/icons/Security";
import SettingsIcon from "@material-ui/icons/Settings";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import WorkIcon from "@material-ui/icons/Work";
import { useAdministratorRoles } from "auth/useAdministratorRoles";
import { AUTH_SIGNOUT } from "auth/AuthorizationTypes";
import { useEmail } from "auth/useEmail";
import { docsService } from "docs/DocsService";
import React, { ReactNode } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { tokens } from "./theme";

const useStyles = makeStyles({
  root: {
    background: tokens.surface2,
    backdropFilter: "blur(16px) saturate(140%)",
    WebkitBackdropFilter: "blur(16px) saturate(140%)",
    borderRight: `1px solid ${tokens.hairline}`,
    display: "flex",
    flexDirection: "column",
    padding: "14px 10px",
    overflowY: "auto",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "2px 6px 14px",
    borderBottom: `1px solid ${tokens.hairline}`,
    marginBottom: 4,
    color: tokens.fg1,
    cursor: "pointer",
    outline: "none",
    "&:hover": {
      color: "hsl(217, 91%, 76%)",
    },
    "&:focus-visible": {
      color: "hsl(217, 91%, 76%)",
    },
  },
  groupLabel: {
    font: `500 10px/1 ${tokens.fontMono}`,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: tokens.fg3,
    padding: "13px 8px 5px",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 8px",
    borderRadius: 6,
    cursor: "pointer",
    font: `500 13px/1 ${tokens.fontSans}`,
    color: tokens.fg2,
    "&:hover": {
      background: "rgba(255,255,255,0.05)",
      color: tokens.fg1,
    },
  },
  itemActive: {
    background: "hsla(217, 91%, 60%, 0.12)",
    color: "hsl(217, 91%, 76%)",
    "&:hover": {
      background: "hsla(217, 91%, 60%, 0.12)",
      color: "hsl(217, 91%, 76%)",
    },
  },
  itemLabel: {
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  user: {
    marginTop: "auto",
    paddingTop: 14,
    borderTop: `1px solid ${tokens.hairline}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "14px 6px 0",
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: `linear-gradient(135deg, ${tokens.primary}, ${tokens.accent})`,
    display: "grid",
    placeItems: "center",
    color: "hsl(220, 30%, 8%)",
    font: `600 11px/1 ${tokens.fontSans}`,
    flex: "none",
  },
});

interface NavItem {
  label: string;
  path?: string;
  exact?: boolean;
  icon: ReactNode;
  onClick?: () => void;
}

function initials(email: string): string {
  const name = email.split("@")[0];
  const parts = name.split(/[._-]/).filter(Boolean);
  const chars =
    parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
  return chars.toUpperCase();
}

export function SideNav() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const email = useEmail();
  const roles = useAdministratorRoles();

  const iconSize = { fontSize: 16 };
  const dashboardGroup: { label: string; items: NavItem[] } = {
    label: "Dashboards",
    items: [
      {
        label: "Overview",
        path: "/dashboards",
        exact: true,
        icon: <DashboardIcon style={iconSize} />,
      },
      {
        label: "Customers",
        path: "/dashboards/customers",
        icon: <PeopleIcon style={iconSize} />,
      },
      {
        label: "Teams",
        path: "/dashboards/teams",
        icon: <ViewModuleIcon style={iconSize} />,
      },
      {
        label: "Engineers",
        path: "/dashboards/engineers",
        icon: <PersonIcon style={iconSize} />,
      },
      {
        label: "Project Managers",
        path: "/dashboards/ems",
        icon: <WorkIcon style={iconSize} />,
      },
      {
        label: "Regions",
        path: "/dashboards/regions",
        icon: <RoomIcon style={iconSize} />,
      },
      {
        label: "Skills",
        path: "/dashboards/skills",
        icon: <LayersIcon style={iconSize} />,
      },
    ],
  };
  const administrationGroup: { label: string; items: NavItem[] } = {
    label: "Administration",
    items: [
      ...(roles.has("superadmin")
        ? [
            {
              label: "Settings",
              path: "/administration/settings",
              icon: <SettingsIcon style={iconSize} />,
            },
            {
              label: "Administrators",
              path: "/administration/administrators",
              icon: <SecurityIcon style={iconSize} />,
            },
            {
              label: "Plugins",
              path: "/administration/plugins",
              icon: <PowerIcon style={iconSize} />,
            },
          ]
        : []),
      {
        label: "Customers",
        path: "/administration/customers",
        icon: <PeopleIcon style={iconSize} />,
      },
      {
        label: "Project Managers",
        path: "/administration/ems",
        icon: <WorkIcon style={iconSize} />,
      },
      {
        label: "Engineers",
        path: "/administration/engineers",
        icon: <PersonIcon style={iconSize} />,
      },
      {
        label: "Regions",
        path: "/administration/regions",
        icon: <RoomIcon style={iconSize} />,
      },
      {
        label: "Teams",
        path: "/administration/teams",
        icon: <ViewModuleIcon style={iconSize} />,
      },
      {
        label: "Sync Status",
        path: "/administration/synchstatus",
        icon: <SyncAltIcon style={iconSize} />,
      },
    ],
  };
  const systemGroup: { label: string; items: NavItem[] } = {
    label: "System",
    items: [
      {
        label: "Help",
        icon: <HelpOutlineIcon style={iconSize} />,
        onClick: () => docsService.getDocs("/index.html"),
      },
    ],
  };
  const groups = [
    location.pathname.startsWith("/administration")
      ? administrationGroup
      : dashboardGroup,
    systemGroup,
  ];

  const isActive = (item: NavItem) => {
    if (!item.path) return false;
    if (item.exact) {
      return (
        location.pathname === item.path || location.pathname === `${item.path}/`
      );
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <Box component="aside" className={classes.root}>
      <div
        className={classes.brand}
        onClick={() => history.push("/dashboards")}
        title="Dashboards"
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            history.push("/dashboards");
          }
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width={26}
          height={26}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
        >
          <circle cx={12} cy={12} r={9} />
          <line x1={3} y1={12} x2={21} y2={12} />
        </svg>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{
              font: `600 13px/1 ${tokens.fontSans}`,
              letterSpacing: "-0.01em",
            }}
          >
            RESOURCE PLANNER
          </span>
          <span
            style={{
              font: `400 10px/1 ${tokens.fontMono}`,
              color: tokens.fg3,
              marginTop: 3,
            }}
          >
            ops · capacity
          </span>
        </div>
      </div>
      {groups.map((group) => (
        <React.Fragment key={group.label}>
          <div className={classes.groupLabel}>{group.label}</div>
          {group.items.map((item) => (
            <div
              key={`${group.label}-${item.label}`}
              className={`${classes.item} ${
                isActive(item) ? classes.itemActive : ""
              }`}
              onClick={
                item.onClick ||
                (() =>
                  history.push({
                    pathname: item.path,
                    search: location.search,
                  }))
              }
            >
              {item.icon}
              <span className={classes.itemLabel}>{item.label}</span>
            </div>
          ))}
        </React.Fragment>
      ))}
      <div className={classes.user}>
        <div className={classes.avatar}>{initials(email)}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          <Typography
            style={{
              font: `500 12px/1.2 ${tokens.fontSans}`,
              color: tokens.fg1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={email}
          >
            {email.split("@")[0]}
          </Typography>
          <Typography
            style={{ font: `400 11px/1 ${tokens.fontMono}`, color: tokens.fg3 }}
          >
            {roles.size > 0 ? Array.from(roles)[0] : "member"}
          </Typography>
        </div>
        <Tooltip title={`Logout (${email})`}>
          <IconButton
            size="small"
            style={{ marginLeft: "auto", color: tokens.fg3 }}
            onClick={() => dispatch({ type: AUTH_SIGNOUT })}
          >
            <ExitToAppIcon style={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </div>
    </Box>
  );
}
